/**
 * App.ts — Root shell composition.
 *
 * Assembles TopBar, ModuleViewport, and BottomNav into the app layout.
 * Listens to router changes and swaps the active module in the viewport.
 */

import type { AweniContext } from './context/AweniContext';
import type { Router } from './router';
import { mountTopBar } from './shell/TopBar';
import { mountBottomNav, setActiveTab } from './shell/BottomNav';
import { mountModuleViewport, setActiveModule } from './shell/ModuleViewport';
import { mountNotificationDrawer, openNotificationDrawer } from './shell/NotificationDrawer';
import { mountProfileSwitcher, toggleProfileSwitcher } from './shell/ProfileSwitcher';
import { getModuleById, getBottomNavModules } from './modules/registry';
import { mountFooter } from './shell/Footer';
import { mountSharedHeader, mountSharedFooter, updateCanonical } from './shell/SharedNavBridge';

/**
 * Mount the full app shell into the given container.
 */
export function mountApp(root: HTMLElement, ctx: AweniContext, router: Router): void {
  root.innerHTML = '';
  root.style.cssText = 'display:flex;flex-direction:column;min-height:100vh;';

  // Guest mode: show the app even without auth. Protected features show login prompts inline.
  // Hard redirect to Identity was causing infinite loops when refresh token bootstrap failed
  // (Cloudflare WAF blocks /identity/token/refresh from some clients → 403 → no token → redirect → loop)
  // Users can click "Log in" from the profile menu or chat auth prompt to authenticate.

  // ── User is authenticated — mount full app shell ──

  // Clean up auth-related hashes (leftover from Identity redirect)
  const currentHash = window.location.hash.replace(/^#\/?/, '');
  const authRoutes = ['login', 'signup', 'forgot-password', 'reset-password', 'verify-email'];
  if (!currentHash || authRoutes.includes(currentHash.split('/')[0])) {
    window.location.hash = '#/home';
  }

  // Auth state change — no action needed in guest mode.
  // Protected features check auth inline and show login prompts.

  // Accessibility skip link
  const skipLink = document.createElement('a');
  skipLink.href = '#aweni-main';
  skipLink.className = 'skip-link';
  skipLink.textContent = ctx.i18n.t('skip_to_content') || 'Skip to content';
  root.appendChild(skipLink);

  // Shared LEARNature-ly header disabled temporarily (2026-04-18) — the dual
  // LN nav + AWENI chrome + iframe interaction caused a navigation cascade
  // where clicking the Home tab navigated the main frame to about:blank.
  // Tracking a cleaner integration in MANUAL-TODO-BOX / IdeasBox.
  // mountSharedHeader(ctx);

  // Top bar
  const topBarEl = document.createElement('header');
  topBarEl.id = 'aweni-topbar';
  root.appendChild(topBarEl);

  // Notification drawer (off-screen overlay)
  const drawerEl = document.createElement('aside');
  drawerEl.id = 'aweni-notifications';
  root.appendChild(drawerEl);

  // Profile switcher (off-screen overlay)
  const profileEl = document.createElement('aside');
  profileEl.id = 'aweni-profile-switcher';
  root.appendChild(profileEl);

  // Module viewport (main content area)
  const viewportEl = document.createElement('main');
  viewportEl.id = 'aweni-main';
  viewportEl.setAttribute('role', 'main');
  viewportEl.className = 'module-viewport';
  viewportEl.style.cssText = 'flex: 1; overflow-y: auto; overflow-x: hidden;';
  root.appendChild(viewportEl);

  // Legal/info footer — appended to viewport AFTER the initial module mounts
  // so it shows at the bottom of scroll (not the top). Re-appended on every
  // module switch to keep it as the last child.
  const footerEl = document.createElement('footer');
  footerEl.id = 'aweni-footer';
  // We push the footer down to clear the 56px fixed bottom nav.
  footerEl.style.cssText = 'flex-shrink:0;padding-bottom:64px;';
  mountFooter(footerEl, ctx);
  // First append happens after the viewport overlays are created; we
  // re-append on each route change (see router.onChange below).

  // Placeholder host for the shared LN footer — currently hidden while we
  // plan a non-iframe-conflicting integration.
  const sharedFooterEl = document.createElement('div');
  sharedFooterEl.id = 'ln-footer-host';
  sharedFooterEl.style.cssText = 'display:none;';
  root.appendChild(sharedFooterEl);

  // Bottom nav
  const bottomNavEl = document.createElement('nav');
  bottomNavEl.id = 'aweni-bottomnav';
  root.appendChild(bottomNavEl);

  // Mount shell components
  mountTopBar(topBarEl, ctx, {
    onNotificationsClick: () => openNotificationDrawer(),
    onProfileClick: () => toggleProfileSwitcher(),
  });

  mountNotificationDrawer(drawerEl, ctx);
  mountProfileSwitcher(profileEl, ctx);
  mountModuleViewport(viewportEl, ctx);

  const navModules = getBottomNavModules();
  mountBottomNav(bottomNavEl, ctx, navModules, (moduleId: string) => {
    // iframe-strategy modules (games, selecto) live at standalone URLs.
    // Navigate the top window directly — skipping the hash intermediate
    // avoids a race where the hashchange + window.location.href fired in
    // the same tick left the browser at about:blank (2026-04-18 regression).
    const mod = getModuleById(moduleId);
    if (mod?.loadStrategy === 'iframe' && mod.entryPoint) {
      window.location.href = mod.entryPoint;
      return;
    }
    router.navigate(`/${moduleId}`);
  });

  // Auth routes that should render full-screen (no shell chrome)
  const FULL_SCREEN_MODULES = new Set(['auth']);

  // React to route changes
  router.onChange((route) => {
    const moduleId = route.module || 'home';
    // For the "more" route without a sub-module, do nothing (popup handles it)
    if (moduleId === 'more') return;

    // Full-screen mode for auth (hide shell chrome)
    if (FULL_SCREEN_MODULES.has(moduleId)) {
      topBarEl.style.display = 'none';
      bottomNavEl.style.display = 'none';
      viewportEl.style.paddingTop = '0';
      viewportEl.style.paddingBottom = '0';
      footerEl.style.display = 'none';
      sharedFooterEl.style.display = 'none';
    } else {
      // Restore display EXPLICITLY. Using '' removes the property entirely,
      // which wipes out the `display: flex` baked into cssText at mount and
      // reverts <nav>/<header> to default block, collapsing the 5 bottom-nav
      // tabs into a stack at left (2026-04-19 "Home only visible" regression).
      topBarEl.style.display = 'flex';
      bottomNavEl.style.display = 'flex';
      viewportEl.style.paddingTop = '';
      viewportEl.style.paddingBottom = '';
      footerEl.style.display = '';
      sharedFooterEl.style.display = '';
    }

    // Update canonical link for SEO dedup — AWENI hash routes that mirror
    // standalone apps point to the canonical standalone URL.
    updateCanonical(moduleId, route.subpath);

    const moduleDef = getModuleById(moduleId);
    if (moduleDef) {
      setActiveModule(moduleId, ctx);
      setActiveTab(moduleId);
      // Re-append the legal/info footer so it stays as the LAST child of
      // the viewport (it scrolls to bottom of page). New module containers
      // get appended by ModuleViewport, so we re-pin the footer after.
      // Use a microtask delay to let the module container append finish first.
      setTimeout(() => {
        if (footerEl.parentElement !== viewportEl) viewportEl.appendChild(footerEl);
        else viewportEl.appendChild(footerEl); // re-append moves to end
      }, 0);
    }
  });

  // Navigate to current hash path or default to home
  const hash = window.location.hash.replace(/^#/, '');
  const initialPath = hash || '/home';
  router.navigate(initialPath);
}
